import { Button } from "@/components/ui/button"
import BridgeTosIframe from "@/onboarding/bridge-tos.iframe"
import { UserKycStatus, User } from "@/api/users"

interface KycProcessScreenProps {
  user: User;
  startKyc: () => Promise<void>;
  isLoadingKyc: boolean;
  onNext: () => void;
  onAcceptTosAndStartKyc: () => void;
  isKycRequired?: boolean;
  // Pass TOS status from parent instead of fetching here
  bridgeTosStatus?: string | null;
  bridgeKycStatus?: string | null;
  bridgeKycLink?: string | null;
  successCtaLabel?: string;
  isTosDialogOpen?: boolean;
  onTosDialogOpenChange?: (open: boolean) => void;
  onTosRedirectBack?: () => void;
}

export default function KycProcessScreen({
  user,
  startKyc,
  isLoadingKyc,
  onNext,
  onAcceptTosAndStartKyc,
  isKycRequired = false,
  bridgeTosStatus,
  bridgeKycStatus,
  bridgeKycLink,
  successCtaLabel,
  isTosDialogOpen,
  onTosDialogOpenChange,
  onTosRedirectBack,
}: KycProcessScreenProps) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[KycProcessScreen] 🎨 RENDERING COMPONENT:`, {
      hasUser: !!user,
      userId: user?.id,
      userKycStatus: user?.kycStatus,
      isLoadingKyc,
      isKycRequired,
      bridgeTosStatus,
      bridgeKycStatus,
      hasBridgeKycLink: !!bridgeKycLink,
      hasUserBridgeKycLink: !!user?.bridgeKycLink,
      hasUserBridgeTosLink: !!user?.bridgeTosLink,
      userBridgeId: user?.bridgeId
    })
  }

  if (isLoadingKyc) {
    // console.log(`[KycProcessScreen] ⏳ SHOWING LOADING STATE - isLoadingKyc is true`)
    // Check if parent is currently verifying the KYC status
    return (
      <div className="text-center">
        <svg
          className="mx-auto h-12 w-12 animate-spin text-blue-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <h3 className="mt-2 text-lg font-medium text-gray-900">
          Verifying Identity Status...
        </h3>
        <p className="mt-1 text-sm text-gray-500">Please wait a moment.</p>
      </div>
    )
  }

  if (!user) {
    // console.log(`[KycProcessScreen] ❌ NO USER DATA - showing loading message`)
    return <div>Loading user information...</div>
  }

  // Case 1: KYC is Successful
  if (user.kycStatus === UserKycStatus.SUCCESS) {
    // console.log(`[KycProcessScreen] ✅ KYC SUCCESSFUL - showing success screen`)
    return (
      <div className="text-center">
        <svg
          className="mx-auto h-12 w-12 text-green-500"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mt-2 text-lg font-medium text-gray-900">
          Identity Verification Successful!
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Your identity has been successfully verified.
        </p>
        <div className="mt-6">
          <Button onClick={() => {
            // console.log(`[KycProcessScreen] 🎯 SUCCESS SCREEN - Continue button clicked`)
            onNext()
          }} data-testid="kyc-success-next-button">
            {successCtaLabel ?? "Continue"}
          </Button>
        </div>
      </div>
    )
  }

  // Case 2: Bridge Terms of Service needs to be accepted
  // Show TOS only if:
  // 1. We have TOS and KYC links available (meaning KYC process was started)
  // 2. TOS status is not approved in Bridge API (either pending or we couldn't check)
  const showTosCondition = user.bridgeTosLink &&
    user.bridgeKycLink &&
    (bridgeTosStatus === "pending" || bridgeTosStatus === null)

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[KycProcessScreen] 📋 TOS CONDITION CHECK:`, {
      hasUserBridgeTosLink: !!user.bridgeTosLink,
      hasUserBridgeKycLink: !!user.bridgeKycLink,
      bridgeTosStatus,
      showTosCondition,
      conditionBreakdown: {
        hasTosLink: !!user.bridgeTosLink,
        hasKycLink: !!user.bridgeKycLink,
        tosStatusPending: bridgeTosStatus === "pending",
        tosStatusNull: bridgeTosStatus === null,
        tosStatusNotApproved: bridgeTosStatus !== "approved"
      }
    })
  }

  if (showTosCondition) {
    // console.log(`[KycProcessScreen] 📄 SHOWING TOS ACCEPTANCE SCREEN`)
    return (
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Terms of Service
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          To proceed with identity verification, please review and accept the
          terms of service from our partner.
        </p>
        <BridgeTosIframe
          bridgeTosLink={user.bridgeTosLink!}
          onAccept={() => {
            // console.log(`[KycProcessScreen] 📝 TOS ACCEPTED - calling onAcceptTosAndStartKyc`)
            onTosDialogOpenChange?.(false)
            onAcceptTosAndStartKyc()
          }}
          onOpenChange={(open) => {
            onTosDialogOpenChange?.(open)
            // console.log(`[KycProcessScreen] 🔄 TOS IFRAME OPEN CHANGE: ${open}`)
          }}
          open={isTosDialogOpen}
          onRedirectBack={onTosRedirectBack}
        />
        {!isKycRequired && (
          <Button onClick={() => {
            // console.log(`[KycProcessScreen] ⏭️ TOS SCREEN - Skip for now clicked`)
            onNext()
          }} variant="outline" className="mt-4">
            Skip for now
          </Button>
        )}
      </div>
    )
  }

  // Case 3: TOS approved but KYC not completed - provide direct access to KYC
  const showContinueKycCondition = user.bridgeId &&
    bridgeTosStatus === "approved" &&
    bridgeKycStatus !== "approved" &&
    bridgeKycStatus !== "rejected" &&
    bridgeKycLink &&
    user.kycStatus === UserKycStatus.PENDING

  // console.log(`[KycProcessScreen] 🔄 CONTINUE KYC CONDITION CHECK:`, {
  //   hasBridgeId: !!user.bridgeId,
  //   bridgeTosStatus,
  //   bridgeKycStatus,
  //   hasBridgeKycLink: !!bridgeKycLink,
  //   userKycStatus: user.kycStatus,
  //   showContinueKycCondition,
  //   conditionBreakdown: {
  //     hasBridgeId: !!user.bridgeId,
  //     tosApproved: bridgeTosStatus === "approved",
  //     kycNotApproved: bridgeKycStatus !== "approved",
  //     kycNotRejected: bridgeKycStatus !== "rejected",
  //     hasKycLink: !!bridgeKycLink,
  //     userKycPending: user.kycStatus === UserKycStatus.PENDING
  //   }
  // })

  if (showContinueKycCondition) {
    // console.log(`[KycProcessScreen] 🎯 SHOWING CONTINUE KYC SCREEN`)
    return (
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900">
          Continue Identity Verification
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          You have accepted our terms of service. You can continue with your
          identity verification process.
        </p>
        <div className="mt-6 space-y-3">
          <Button
            onClick={() => {
              // console.log(`[KycProcessScreen] 🚀 CONTINUE KYC - Continue button clicked`)
              // Use the parent callback instead of direct navigation
              // This ensures proper new-tab handling and flow control
              onAcceptTosAndStartKyc()
            }}
            className="w-full"
            data-testid="continue-kyc-button"
          >
            Continue Identity Verification
          </Button>
          {!isKycRequired && (
            <Button
              onClick={() => {
                // console.log(`[KycProcessScreen] ⏭️ CONTINUE KYC - Continue later clicked`)
                onNext()
              }}
              variant="outline"
              className="w-full"
              data-testid="kyc-continue-later-button"
            >
              Continue Later
            </Button>
          )}
        </div>
      </div>
    )
  }

  // Case 4: KYC is Pending (general case)
  // Show this if TOS has been completed but KYC is still pending
  if (user.kycStatus === UserKycStatus.PENDING) {
    // console.log(`[KycProcessScreen] ⏳ SHOWING KYC PENDING SCREEN`)
    return (
      <div className="text-center">
        <svg
          className="mx-auto h-12 w-12 text-yellow-500"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="mt-2 text-lg font-medium text-gray-900">
          Identity Verification Pending
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Your identity verification is currently under review. We will notify
          you of any updates. You can continue with the onboarding process.
        </p>
        <div className="mt-6">
          <Button onClick={() => {
            // console.log(`[KycProcessScreen] ⏭️ PENDING SCREEN - Continue button clicked`)
            onNext()
          }} data-testid="kyc-pending-next-button">
            Continue
          </Button>
        </div>
      </div>
    )
  }

  // Case 5: KYC Failed or Ready to Start/Restart
  // console.log(`[KycProcessScreen] 🚀 SHOWING START/RESTART KYC SCREEN:`, {
  //   userKycStatus: user.kycStatus,
  //   isFailed: user.kycStatus === UserKycStatus.FAILED
  // })
  return (
    <div className="text-center">
      <h3 className="text-lg font-medium text-gray-900">
        Identity Verification
      </h3>
      <p className="mt-1 text-sm text-gray-600">
        To comply with regulations and ensure security, we need to verify your
        identity. This process is handled by our trusted partner.
      </p>
      {user.kycStatus === UserKycStatus.FAILED && (
        <p className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded-md">
          Your previous identity verification attempt failed. Please try again
          or contact support if the issue persists.
        </p>
      )}
      <div className="mt-6 space-y-3">
        <Button
          onClick={() => {
            // console.log(`[KycProcessScreen] 🚀 START KYC - Button clicked:`, {
            //   isFailed: user.kycStatus === UserKycStatus.FAILED,
            //   buttonText: user.kycStatus === UserKycStatus.FAILED ? "Try Verification Again" : "Start Identity Verification"
            // })
            startKyc()
          }}
          disabled={isLoadingKyc}
          className="w-full"
          data-testid="start-kyc-button"
        >
          {isLoadingKyc
            ? "Processing..."
            : user.kycStatus === UserKycStatus.FAILED
              ? "Try Verification Again"
              : "Start Identity Verification"}
        </Button>
        {!isKycRequired && (
          <Button
            onClick={() => {
              // console.log(`[KycProcessScreen] ⏭️ START KYC - Skip for now clicked`)
              onNext()
            }}
            variant="outline"
            className="w-full"
            data-testid="kyc-skip-button"
          >
            Skip for now
          </Button>
        )}
      </div>
      <p className="mt-4 text-xs text-gray-500">
        You are about to be redirected to our partner's secure platform to
        complete this step.
      </p>
    </div>
  )
}
